import { isMicAlias, type ActiveMic, type MicChoice, type MicInputs } from '@clarix/meeting-recording';
import type { AppSelectOption } from '@clarix/ui-shared';
import { QM_MIC } from './constants';
import { QM_COPY } from './copy';
import type { MicDevices } from './useMicDevices';

// The alias label ("Default - …") is localized, so a device's own name always comes from the real
// device with the same groupId, never from editing the label. Ambiguous matches resolve to nothing.

function onlyMatch(devices: MediaDeviceInfo[], groupId: string | undefined): MediaDeviceInfo | undefined {
  if (!groupId) return undefined;
  const matches = devices.filter((d) => d.groupId === groupId);
  return matches.length === 1 ? matches[0] : undefined;
}

/** The real device the OS default currently points at. */
export function defaultDevice({ devices, systemDefault }: MicInputs): MediaDeviceInfo | undefined {
  return onlyMatch(devices, systemDefault?.groupId);
}

/**
 * What the OS default points at, comparable across reads: Chrome can re-salt every groupId once a
 * capture opens, so it is the real device's id, else the alias label (compared, never parsed).
 */
export function defaultKey(inputs: MicInputs): string | null {
  return defaultDevice(inputs)?.deviceId ?? inputs.systemDefault?.label ?? null;
}

/** The device a saved choice opens, matched the way `openMic` does: by id, then by label. */
function savedDevice({ devices }: MicInputs, saved: MicChoice): MediaDeviceInfo | undefined {
  return devices.find((d) => d.deviceId === saved.deviceId) ?? (saved.label ? devices.find((d) => d.label === saved.label) : undefined);
}

/** The real device behind a live track (the track of the `'default'` alias reports `'default'`). */
function deviceOf({ devices }: MicInputs, mic: ActiveMic): MediaDeviceInfo | undefined {
  return devices.find((d) => d.deviceId === mic.deviceId) ?? onlyMatch(devices, mic.groupId);
}

export function micName(inputs: MicInputs, mic: ActiveMic): string {
  return deviceOf(inputs, mic)?.label || mic.label || QM_COPY.unnamedMic;
}

/** A live mic as a choice that reopens that same device; null when the device can't be pinned down. */
export function choiceFor(inputs: MicInputs, mic: ActiveMic): MicChoice | null {
  const device = isMicAlias(mic.deviceId) ? onlyMatch(inputs.devices, mic.groupId) : deviceOf(inputs, mic);
  return device ? { deviceId: device.deviceId, label: device.label } : null;
}

export function micOptions(inputs: MicDevices, saved: MicChoice | null): AppSelectOption[] {
  const fallbackName = defaultDevice(inputs)?.label;
  const options: AppSelectOption[] = [
    { value: QM_MIC.SYSTEM_DEFAULT, label: fallbackName ? QM_COPY.systemDefaultOf(fallbackName) : QM_COPY.systemDefault },
    ...inputs.devices.map((d) => ({ value: d.deviceId, label: d.label || QM_COPY.unnamedMic })),
  ];
  if (saved && !savedDevice(inputs, saved)) {
    const label = saved.label ?? QM_COPY.savedMic;
    // Unlisted (no permission yet) it may well be connected; listed and absent, it is not.
    options.push(
      inputs.listed
        ? { value: saved.deviceId, label: QM_COPY.micNotConnected(label), disabled: true }
        : { value: saved.deviceId, label },
    );
  }
  return options;
}

/** Before recording: the saved choice as a start would resolve it, else the system default. */
export function savedValue(inputs: MicDevices, saved: MicChoice | null): string {
  if (!saved) return QM_MIC.SYSTEM_DEFAULT;
  return savedDevice(inputs, saved)?.deviceId ?? saved.deviceId;
}

/** During a recording: the option of the device capturing, by deviceId then groupId; '' if neither. */
export function activeValue(inputs: MicDevices, active: ActiveMic | null): string {
  if (!active) return '';
  if (active.deviceId === QM_MIC.SYSTEM_DEFAULT) return QM_MIC.SYSTEM_DEFAULT;
  return deviceOf(inputs, active)?.deviceId ?? '';
}
